/**
 * VoiceClonePage
 *
 * Page for cloning a new voice by uploading audio samples.
 * Uses ElevenLabs API via Convex action.
 */

import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { PostWriterSubmenuMemo } from '@/components/post-writer/PostWriterSubmenu';
import { VoiceCloneForm } from '@/components/voices/VoiceCloneForm';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { useClonedVoices } from '@/hooks/useClonedVoices';
import { ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';
import type { IVoiceCloneFormInput } from '@/@types/voices';
import { VOICE_CONSTANTS } from '@/@types/voices';

export const VoiceClonePage = () => {
  const navigate = useNavigate();

  // Hook for cloning
  const {
    isCloning,
    cloneStep,
    cloneError,
    uploadProgress,
    voiceCount,
    canCreateVoice,
    cloneVoice,
    reset,
  } = useClonedVoices({
    onCloneSuccess: () => {
      // Navigate to My Voices after successful clone
      setTimeout(() => {
        navigate('/voices/my-voices');
      }, 1500);
    },
  });

  // Handle form submission
  const handleSubmit = useCallback(
    async (input: IVoiceCloneFormInput) => {
      await cloneVoice(input);
    },
    [cloneVoice]
  );

  // Handle cancel / back navigation
  const handleBack = useCallback(() => {
    reset();
    navigate('/voices/my-voices');
  }, [navigate, reset]);

  // Get step display info
  const getStepInfo = () => {
    switch (cloneStep) {
      case 'validating':
        return { label: 'Validating files...', progress: 10 };
      case 'uploading':
        return { label: 'Uploading audio...', progress: uploadProgress };
      case 'cloning':
        return { label: 'Cloning voice with ElevenLabs...', progress: 80 };
      case 'completed':
        return { label: 'Voice cloned successfully!', progress: 100 };
      case 'error':
        return { label: 'Error occurred', progress: 0 };
      default:
        return { label: '', progress: 0 };
    }
  };

  const stepInfo = getStepInfo();

  return {
    submenu: <PostWriterSubmenuMemo />,
    mainContent: (
      <div className="h-full bg-white dark:bg-gray-950">
        <div className="p-6 max-w-2xl mx-auto">
          {/* Back Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBack}
            className="mb-4 -ml-2"
            disabled={isCloning}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to My Voices
          </Button>

          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Clone Voice</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Upload audio samples to create your cloned voice.{' '}
              <span className="text-gray-400">
                ({voiceCount}/{VOICE_CONSTANTS.MAX_VOICES_PER_USER} voices used)
              </span>
            </p>
          </div>

          {/* Limit Warning */}
          {!canCreateVoice && (
            <div className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-900 rounded-lg">
              <p className="text-sm text-yellow-700 dark:text-yellow-400">
                You&apos;ve reached the maximum of {VOICE_CONSTANTS.MAX_VOICES_PER_USER} voices.
                Delete an existing voice to create a new one.
              </p>
            </div>
          )}

          {/* Progress Display (when cloning) */}
          {isCloning && (
            <Card className="mb-6">
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="animate-pulse">
                      <div className="h-3 w-3 bg-blue-500 rounded-full" />
                    </div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {stepInfo.label}
                    </p>
                  </div>
                  <Progress value={stepInfo.progress} className="h-2" />
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    This may take a minute. Please don&apos;t close this page.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Success Message */}
          {cloneStep === 'completed' && (
            <Card className="mb-6 border-green-200 dark:border-green-900">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                  <div>
                    <p className="text-sm font-medium text-green-700 dark:text-green-300">
                      Voice cloned successfully!
                    </p>
                    <p className="text-xs text-green-600 dark:text-green-400">
                      Redirecting to My Voices...
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Error Message */}
          {cloneStep === 'error' && cloneError && (
            <Card className="mb-6 border-red-200 dark:border-red-900">
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-red-700 dark:text-red-300">
                      Failed to clone voice
                    </p>
                    <p className="text-xs text-red-600 dark:text-red-400 mt-1">{cloneError}</p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={reset}
                      className="mt-3"
                    >
                      Try Again
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Clone Form */}
          {cloneStep !== 'completed' && (
            <Card>
              <CardContent className="pt-6">
                <VoiceCloneForm
                  onSubmit={handleSubmit}
                  isSubmitting={isCloning}
                  onCancel={handleBack}
                />
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    ),
  };
};
